import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'membership-skip-payment';
const SKIP_KEYWORDS_STORAGE_KEY = 'membership-skip-keywords';

type MembershipWizardContextValue = {
  skipPaymentStep: boolean;
  setSkipPaymentStep: (skip: boolean) => void;
  skipKeywordsStep: boolean;
  setSkipKeywordsStep: (skip: boolean) => void;
};

const MembershipWizardContext =
  createContext<MembershipWizardContextValue | null>(null);

function readSkipPaymentStep(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSkipPaymentStep(skip: boolean) {
  try {
    if (skip) {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore storage errors
  }
}

function readSkipKeywordsStep(): boolean {
  try {
    return sessionStorage.getItem(SKIP_KEYWORDS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSkipKeywordsStep(skip: boolean) {
  try {
    if (skip) {
      sessionStorage.setItem(SKIP_KEYWORDS_STORAGE_KEY, '1');
    } else {
      sessionStorage.removeItem(SKIP_KEYWORDS_STORAGE_KEY);
    }
  } catch {
    // ignore storage errors
  }
}

export function MembershipWizardProvider({ children }: { children: ReactNode }) {
  const [skipPaymentStep, setSkipPaymentStepState] = useState(readSkipPaymentStep);
  const [skipKeywordsStep, setSkipKeywordsStepState] = useState(readSkipKeywordsStep);

  const setSkipPaymentStep = useCallback((skip: boolean) => {
    setSkipPaymentStepState(skip);
    writeSkipPaymentStep(skip);
  }, []);

  const setSkipKeywordsStep = useCallback((skip: boolean) => {
    setSkipKeywordsStepState(skip);
    writeSkipKeywordsStep(skip);
  }, []);

  const value = useMemo(
    () => ({
      skipPaymentStep,
      setSkipPaymentStep,
      skipKeywordsStep,
      setSkipKeywordsStep,
    }),
    [skipPaymentStep, setSkipPaymentStep, skipKeywordsStep, setSkipKeywordsStep],
  );

  return (
    <MembershipWizardContext.Provider value={value}>
      {children}
    </MembershipWizardContext.Provider>
  );
}

export function useMembershipWizard(): MembershipWizardContextValue | null {
  return useContext(MembershipWizardContext);
}
